import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { Box, Button, useMediaQuery, useTheme } from '@mui/material';
import CustomDialog from '@ui/components/common/Dialog';
import BDrawer from '@ui/components/common/BDrawer';
import { HFilledTextField } from '@ui/components/common/TextField';
import { useTranslation } from 'react-i18next';
import { PhoneInputMasking } from '@ui/components/PhoneMasking/PhoneInputMasking';
import { useForm } from 'react-hook-form';
import { SingleSelect } from '@ui/components/common/Autocomplete';
import { CompanyRegion } from '@application/enums/company-region-enum';
import ImageUpload from '@ui/pages/Users/AddEditUser/components/ImageUpload';
const LOGO_IMAGE_TYPES = ['JPG', 'JPEG', 'PNG', 'GIF', 'WEBP'];
const isFile = (value) => value instanceof File ||
    (typeof value === 'object' &&
        value !== null &&
        'name' in value &&
        'size' in value &&
        'type' in value);
const EditCompanyDialog = ({ open, company, onClose, onSave }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
    const [formValues, setFormValues] = useState(company);
    const { control, getValues, reset } = useForm({
        defaultValues: {
            phone: company?.phone ?? '',
            region: company?.region ?? '',
            logo: company?.logoUrl ?? null
        }
    });
    useEffect(() => {
        setFormValues(company);
        reset({
            phone: company?.phone ?? '',
            region: company?.region ?? '',
            logo: company?.logoUrl ?? null
        });
    }, [company, reset]);
    const regionOptions = Object.keys(CompanyRegion)
        .filter((key) => isNaN(Number(key)) && key !== 'Unknown')
        .map((key) => ({ label: key, value: key }));
    const handleChange = (field, value) => {
        setFormValues((prev) => (prev ? { ...prev, [field]: value } : prev));
    };
    const handleSubmit = () => {
        if (!formValues)
            return;
        const logoValue = getValues('logo');
        onSave({
            ...formValues,
            phone: getValues('phone') ?? '',
            region: getValues('region') ?? '',
            logoFile: isFile(logoValue) ? logoValue : undefined
        });
    };
    if (!formValues)
        return null;
    const content = (_jsxs(Box, { sx: { p: 3, display: 'flex', flexDirection: 'column', gap: 2 }, children: [_jsx(ImageUpload, { control: control, name: 'logo', edit: true, centerVariant: true, types: LOGO_IMAGE_TYPES, label: t('settings.companyManagement.uploadLogo') }), _jsx(HFilledTextField, { label: t('settings.companyManagement.name'), value: formValues.name, onChange: (event) => handleChange('name', event.target.value), bg: 'grey8', fullWidth: true }), _jsx(HFilledTextField, { label: t('settings.companyManagement.email'), value: formValues.email, onChange: (event) => handleChange('email', event.target.value), bg: 'grey8', fullWidth: true }), _jsx(PhoneInputMasking, { control: control, name: 'phone', label: t('settings.companyManagement.phone'), bg: 'grey8' }), _jsx(SingleSelect, { bg: 'grey8', control: control, name: 'region', label: t('settings.companyManagement.region'), options: regionOptions })] }));
    const actions = (_jsxs(_Fragment, { children: [_jsx(Button, { fullWidth: true, variant: 'outlined', color: 'inherit', size: 'large', onClick: onClose, children: t('actions.cancel') }), _jsx(Button, { fullWidth: true, variant: 'contained', color: 'inherit', size: 'large', onClick: handleSubmit, children: t('settings.companyManagement.editCompanyButton') })] }));
    if (isMobile) {
        return (_jsx(BDrawer, { open: open, onClose: onClose, title: t('settings.companyManagement.editCompany'), dialogActions: actions, children: content }));
    }
    return (_jsx(CustomDialog, { open: open, onClose: onClose, title: t('settings.companyManagement.editCompany'), backgroundVariant: 'white', closeIcon: true, actions: actions, children: content }));
};
export default EditCompanyDialog;
